import React, { useEffect } from 'react';
import styles from '../../styles/modalwrapper.module.scss';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ModalWrapper = ({ 
  isOpen = false, 
  onClose, 
  title, 
  children, 
  footer,
  size = 'medium', // small, medium, large, fullscreen
  closeOnBackdrop = false,
  closeOnEsc = true,
  showCloseButton = true,
  closeDisabled = false,
  className = '',
  headerClassName = '',
  backdropClassName = '',
  type, // warning, info, error, success, etc.
  border = true // show/hide borders on header and footer
}) => {
  const canClose = typeof onClose === 'function' && !closeDisabled;

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEsc || !canClose) return;

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, closeOnEsc, canClose, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && closeOnBackdrop && canClose) {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className={`${styles.modalBackdrop} ${backdropClassName}`} onClick={handleBackdropClick}>
      <div className={`${styles.modalContainer} ${styles[`modal--${size}`]} ${className}`}>
        {/* Header */}
        {(title || showCloseButton) && (
          <div className={`${styles.modalHeader} ${!border ? styles.modalHeaderBorderless : ''} ${headerClassName}`}>
            {title && (
              <div className={styles.modalTitleWrapper}>
                {type === 'warning' && (
                  <ExclamationTriangleIcon className={`${styles.warningIcon} icon-lg`} />
                )}
                {type === 'info' && (
                  <InformationCircleIcon className={`${styles.infoIcon} icon-lg`} />
                )}
                <h2 className={styles.modalTitle}>{title}</h2>
              </div>
            )}
            {showCloseButton && (
              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={canClose ? onClose : undefined}
                disabled={closeDisabled || !canClose}
                aria-label="Close modal"
                aria-disabled={closeDisabled || !canClose}
              >
                <XMarkIcon className={styles.modalCloseIcon} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={styles.modalBody}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={`${styles.modalFooter} ${!border ? styles.modalFooterBorderless : ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalWrapper;
